import { useState, useEffect, useRef } from 'react';
import { adminApi, type AdminUser, type UserDetail } from '@/lib/admin';
import { useToast } from '@/components/base/Toast';
import { SectionCard, StatusBadge, EmptyState, LoadingState, ErrorState } from '@/pages/admin/components/UI';

const verifiedMaps = {
  true: { label: '已认证', className: 'bg-green-100 text-green-600', icon: 'ri-check-line' },
  false: { label: '未认证', className: 'bg-red-100 text-red-500', icon: 'ri-close-line' },
  default: { label: '未知', className: 'bg-brown-100 text-brown-600', icon: 'ri-question-line' },
};

const roleMaps = {
  admin: { label: '管理员', className: 'bg-gold-100 text-gold-700', icon: 'ri-admin-line' },
  user: { label: '用户', className: 'bg-brown-100 text-brown-600', icon: 'ri-user-line' },
  default: { label: '用户', className: 'bg-brown-100 text-brown-600', icon: 'ri-user-line' },
};

export default function AdminUsers() {
  const { showToast } = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [detailTarget, setDetailTarget] = useState<AdminUser | null>(null);
  const [detailData, setDetailData] = useState<UserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRefreshing, setDetailRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<string>('');
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const deletingRef = useRef(false);
  const [holdingLimitInput, setHoldingLimitInput] = useState('');
  const [savingHoldingLimit, setSavingHoldingLimit] = useState(false);
  const [autoMarginEnabled, setAutoMarginEnabled] = useState(false);
  const [savingAutoMargin, setSavingAutoMargin] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await adminApi.listUsers();
      console.log('[AdminUsers] listUsers success, count:', data?.length);
      setUsers(data);
    } catch (e) {
      console.error('[AdminUsers] listUsers error:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 拉取用户详情；mode='open' 为首次打开(带骨架屏)，'refresh' 为静默刷新(不闪屏、不覆盖正在输入的内容)
  const fetchDetail = async (user: AdminUser, mode: 'open' | 'refresh' = 'open') => {
    if (mode === 'open') {
      setDetailLoading(true);
      setDetailData(null);
    } else {
      setDetailRefreshing(true);
    }
    try {
      // 每次打开/刷新都强制向后端取一次最新价（后端会实时调用行情源）
      const data = await adminApi.getUserDetail(user.id);
      setDetailData(data);
      if (mode === 'open') {
        setHoldingLimitInput(data.profile.max_holding_weight != null ? String(data.profile.max_holding_weight) : '');
      }
      setLastRefreshTime(new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    } catch (e) {
      if (mode === 'open') {
        const msg = e instanceof Error ? e.message : '加载失败';
        showToast(msg, 'error');
        console.error('getUserDetail error:', e);
        setDetailTarget(null);
      }
    } finally {
      setDetailLoading(false);
      setDetailRefreshing(false);
    }
  };

  const handleViewDetail = (user: AdminUser) => {
    setDetailTarget(user);
    setAutoMarginEnabled(!!user.auto_margin_enabled);
    fetchDetail(user, 'open');
  };

  // 详情弹窗打开时，每 30 秒静默刷新一次当前价/盈亏（不闪屏）
  useEffect(() => {
    if (!detailTarget) return;
    const timer = setInterval(() => {
      fetchDetail(detailTarget, 'refresh');
    }, 30 * 1000);
    return () => clearInterval(timer);
  }, [detailTarget]);

  const openDeleteConfirm = (user: AdminUser) => {
    setDeleteTarget(user);
    setDeleteConfirmText('');
  };

  const handleDelete = async () => {
    if (!deleteTarget || deletingRef.current) return;
    deletingRef.current = true;
    setDeleting(true);
    try {
      await adminApi.deleteUser(deleteTarget.id);
      showToast('用户已删除', 'success');
      setDeleteTarget(null);
      setDeleteConfirmText('');
      await fetchData();
    } catch (e) {
      const msg = e instanceof Error ? e.message : '删除失败';
      showToast(msg, 'error');
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };

  const handleSaveHoldingLimit = async (explicitValue?: number | null) => {
    if (!detailTarget || savingHoldingLimit) return;
    let value: number | null;
    if (explicitValue !== undefined) {
      value = explicitValue;
    } else {
      const trimmed = holdingLimitInput.trim();
      if (trimmed === '') {
        value = null;
      } else {
        const n = Number(trimmed);
        if (Number.isNaN(n) || n <= 0) {
          showToast('持仓上限需为正数', 'error');
          return;
        }
        value = n;
      }
    }
    setSavingHoldingLimit(true);
    try {
      const res = await adminApi.setHoldingLimit(detailTarget.id, value);
      const savedValue = res.max_holding_weight;
      showToast(savedValue === null || savedValue === undefined ? '已取消持仓限制' : `持仓上限已设为 ${savedValue}g`, 'success');
      setHoldingLimitInput(savedValue != null ? String(savedValue) : '');
      setDetailData((prev) => (prev ? { ...prev, profile: { ...prev.profile, max_holding_weight: savedValue ?? null } } : prev));
      fetchData();
    } catch (e) {
      showToast(e instanceof Error ? e.message : '保存失败', 'error');
    } finally {
      setSavingHoldingLimit(false);
    }
  };

  const handleToggleAutoMargin = async () => {
    if (!detailTarget || savingAutoMargin) return;
    const next = !autoMarginEnabled;
    setSavingAutoMargin(true);
    try {
      await adminApi.setAutoMargin(detailTarget.id, next);
      setAutoMarginEnabled(next);
      showToast(next ? '已开启自动补充保证金' : '已关闭自动补充保证金', 'success');
      fetchData();
    } catch (e) {
      showToast(e instanceof Error ? e.message : '操作失败', 'error');
    } finally {
      setSavingAutoMargin(false);
    }
  };

  const filtered = users.filter((u) => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return true;
    return (
      (u.phone || '').includes(kw) ||
      (u.name || '').includes(kw) ||
      (u.email || '').toLowerCase().includes(kw)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-brown-900 text-lg font-bold">用户管理</h2>
          <p className="text-brown-400 text-xs mt-0.5">共 {users.length} 位用户</p>
        </div>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-brown-400">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索手机号/姓名"
            className="w-full sm:w-64 pl-9 pr-3 py-2 rounded-lg border border-brown-200 bg-white text-sm text-brown-800 outline-none focus:ring-2 focus:ring-gold-400"
          />
        </div>
      </div>

      <SectionCard>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState onRetry={fetchData} />
        ) : filtered.length === 0 ? (
          <EmptyState title="暂无用户" desc="还没有匹配的用户" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-brown-400 text-xs border-b border-brown-100">
                  <th className="px-4 md:px-5 py-3 font-medium">用户</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">认证</th>
                  <th className="px-4 py-3 font-medium">会员等级</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">余额</th>
                  <th className="px-4 py-3 font-medium">角色</th>
                  <th className="px-4 py-3 font-medium text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-b border-brown-50 last:border-0 hover:bg-brown-50/40 transition-colors">
                    <td className="px-4 md:px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gold-100 flex items-center justify-center flex-shrink-0">
                          <span className="w-5 h-5 flex items-center justify-center text-gold-700">
                            <i className="ri-user-fill text-base"></i>
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-brown-900 font-medium truncate">
                            {u.name || '未填写姓名'}
                          </p>
                          <p className="text-brown-400 text-xs truncate">{u.phone || u.email || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell">
                      <StatusBadge status={String(u.verified)} maps={verifiedMaps} />
                    </td>
                    <td className="px-4 py-3.5 text-brown-700">{u.member_level || '普通会员'}</td>
                    <td className="px-4 py-3.5 hidden sm:table-cell text-brown-700">
                      ¥{u.balance.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusBadge status={u.role} maps={roleMaps} />
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => handleViewDetail(u)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gold-200 text-gold-600 text-xs cursor-pointer whitespace-nowrap hover:bg-gold-50 transition-colors"
                        >
                          <span className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-eye-line text-xs"></i>
                          </span>
                          查看详情
                        </button>
                        <button
                          onClick={() => openDeleteConfirm(u)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-red-200 text-red-500 text-xs cursor-pointer whitespace-nowrap hover:bg-red-50 transition-colors"
                        >
                          <span className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-delete-bin-line text-xs"></i>
                          </span>
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* 用户详情弹窗 */}
      {detailTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setDetailTarget(null); setDetailData(null); }}></div>
          <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto animate-fade-up">
            {detailLoading ? (
              <LoadingState />
            ) : detailData ? (
              <>
                {/* Header */}
                <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-5 py-4 border-b border-brown-100">
                  <h3 className="text-brown-900 text-base font-bold">用户详情</h3>
                  <div className="flex items-center gap-3">
                    {lastRefreshTime && (
                      <span className="text-brown-400 text-xs whitespace-nowrap">
                        {detailRefreshing ? '刷新中...' : `更新于 ${lastRefreshTime}`}
                      </span>
                    )}
                    <button
                      onClick={() => fetchDetail(detailTarget, 'refresh')}
                      disabled={detailRefreshing}
                      title="刷新当前价"
                      className="w-8 h-8 flex items-center justify-center text-brown-400 hover:text-gold-600 cursor-pointer rounded-full hover:bg-gold-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className={`ri-refresh-line text-lg ${detailRefreshing ? 'animate-spin' : ''}`}></i>
                    </button>
                    <button
                      onClick={() => { setDetailTarget(null); setDetailData(null); }}
                      className="w-8 h-8 flex items-center justify-center text-brown-400 hover:text-brown-600 cursor-pointer rounded-full hover:bg-brown-50 transition-colors"
                    >
                      <i className="ri-close-line text-lg"></i>
                    </button>
                  </div>
                </div>

                <div className="p-5 space-y-5">
                  {/* 用户基础信息 */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gold-100 flex items-center justify-center flex-shrink-0">
                      <span className="w-6 h-6 flex items-center justify-center text-gold-700">
                        <i className="ri-user-fill text-lg"></i>
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-brown-900 font-bold text-base">
                        {detailData.profile.name || '未填写姓名'}
                      </p>
                      <p className="text-brown-400 text-xs mt-0.5">
                        {detailData.profile.phone || detailData.profile.email || '—'} · {detailData.profile.member_level || '普通会员'}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-brown-400 text-xs">账户余额</p>
                      <p className="text-brown-900 font-bold">
                        ¥{detailData.profile.balance.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>

                  {/* 实名认证信息 */}
                  <div className="rounded-lg border border-brown-100 bg-brown-50/40 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-brown-900 text-sm font-bold flex items-center gap-2">
                        <span className="w-4 h-4 flex items-center justify-center text-gold-600">
                          <i className="ri-id-card-line text-sm"></i>
                        </span>
                        实名认证信息
                      </h4>
                      <StatusBadge status={String(detailData.profile.verified)} maps={verifiedMaps} />
                    </div>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 text-sm">
                      <span className="text-brown-500 text-xs">姓名：<span className="text-brown-900 font-medium">{detailData.profile.name || '—'}</span></span>
                      <span className="text-brown-500 text-xs">身份证号：<span className="text-brown-900 font-medium">{detailData.profile.id_card || '—'}</span></span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <p className="text-brown-500 text-xs mb-1.5">身份证正面</p>
                        {detailData.profile.id_card_front ? (
                          <button
                            type="button"
                            onClick={() => setPreviewImage(detailData.profile.id_card_front)}
                            className="w-full bg-white rounded-lg overflow-hidden border border-brown-100 cursor-pointer hover:opacity-90 transition-opacity"
                          >
                            <img src={detailData.profile.id_card_front} alt="身份证正面" className="w-full h-36 object-contain bg-brown-50" />
                          </button>
                        ) : (
                          <div className="h-36 bg-brown-50 rounded-lg border border-brown-100 flex items-center justify-center text-brown-400">
                            <span className="w-6 h-6 flex items-center justify-center"><i className="ri-image-line text-xl"></i></span>
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-brown-500 text-xs mb-1.5">身份证反面</p>
                        {detailData.profile.id_card_back ? (
                          <button
                            type="button"
                            onClick={() => setPreviewImage(detailData.profile.id_card_back)}
                            className="w-full bg-white rounded-lg overflow-hidden border border-brown-100 cursor-pointer hover:opacity-90 transition-opacity"
                          >
                            <img src={detailData.profile.id_card_back} alt="身份证反面" className="w-full h-36 object-contain bg-brown-50" />
                          </button>
                        ) : (
                          <div className="h-36 bg-brown-50 rounded-lg border border-brown-100 flex items-center justify-center text-brown-400">
                            <span className="w-6 h-6 flex items-center justify-center"><i className="ri-image-line text-xl"></i></span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 盈亏汇总 — 6个卡片 */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className={`rounded-lg p-3 text-center border ${detailData.summary.total_floating_profit >= 0 ? 'bg-red-50 border-red-100' : 'bg-emerald-50 border-emerald-100'}`}>
                      <p className={`text-[10px] mb-1 ${detailData.summary.total_floating_profit >= 0 ? 'text-red-500' : 'text-emerald-600'}`}>实时浮动盈亏</p>
                      <p className={`text-lg font-bold ${detailData.summary.total_floating_profit >= 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                        {detailData.summary.total_floating_profit >= 0 ? '+' : ''}
                        {detailData.summary.total_floating_profit.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className={`rounded-lg p-3 text-center border ${detailData.summary.total_profit > 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
                      <p className={`text-[10px] mb-1 ${detailData.summary.total_profit > 0 ? 'text-emerald-600' : 'text-red-500'}`}>累计盈亏</p>
                      <p className={`text-lg font-bold ${detailData.summary.total_profit > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {detailData.summary.total_profit > 0 ? '-' : '+'}
                        ¥{Math.abs(detailData.summary.total_profit).toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="bg-gold-50 rounded-lg p-3 text-center border border-gold-100">
                      <p className="text-gold-600 text-[10px] mb-1">持仓克重</p>
                      <p className="text-brown-900 text-lg font-bold">
                        {detailData.summary.total_position_weight.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}g
                      </p>
                    </div>
                    <div className="bg-brown-50 rounded-lg p-3 text-center border border-brown-100">
                      <p className="text-brown-500 text-[10px] mb-1">净资产</p>
                      <p className="text-brown-900 text-lg font-bold">
                        ¥{detailData.summary.net_asset_value.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center border border-green-100">
                      <p className="text-green-600 text-[10px] mb-1">总入金</p>
                      <p className="text-brown-900 text-lg font-bold">
                        ¥{detailData.summary.total_deposit_amount.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center border border-red-100">
                      <p className="text-red-500 text-[10px] mb-1">总出金</p>
                      <p className="text-brown-900 text-lg font-bold">
                        ¥{detailData.summary.total_withdraw_amount.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>

                  {/* 持仓上限设置 */}
                  <div className="rounded-lg border border-brown-100 bg-brown-50/40 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-brown-900 text-sm font-bold flex items-center gap-2">
                          <span className="w-4 h-4 flex items-center justify-center text-gold-600">
                            <i className="ri-scales-3-line text-sm"></i>
                          </span>
                          持仓上限设置
                        </h4>
                        <p className="text-brown-400 text-xs mt-0.5">设置后，该用户净持仓超过上限将无法继续买入</p>
                      </div>
                      <span className="text-brown-500 text-xs whitespace-nowrap">
                        当前持仓{' '}
                        <span className="text-brown-900 font-bold">
                          {detailData.summary.holding_weight.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}g
                        </span>
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <div className="relative flex-1">
                        <input
                          type="number"
                          min={1}
                          value={holdingLimitInput}
                          onChange={(e) => setHoldingLimitInput(e.target.value)}
                          placeholder="留空表示不限制"
                          className="w-full px-3 py-2 pr-8 rounded-lg border border-brown-200 bg-white text-sm text-brown-800 outline-none focus:ring-2 focus:ring-gold-400"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-brown-400 text-xs">g</span>
                      </div>
                      <button
                        onClick={() => handleSaveHoldingLimit()}
                        disabled={savingHoldingLimit}
                        className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-gold-500 text-brown-950 text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-gold-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {savingHoldingLimit ? '保存中...' : '保存'}
                      </button>
                      {detailData.profile.max_holding_weight != null && (
                        <button
                          onClick={() => handleSaveHoldingLimit(null)}
                          disabled={savingHoldingLimit}
                          className="inline-flex items-center justify-center px-3 py-2 rounded-lg border border-brown-200 text-brown-500 text-xs cursor-pointer whitespace-nowrap hover:bg-brown-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          取消限制
                        </button>
                      )}
                    </div>
                    {detailData.profile.max_holding_weight != null && (
                      <p className="text-gold-600 text-xs mt-2">
                        当前上限：{detailData.profile.max_holding_weight}g
                      </p>
                    )}
                  </div>

                  {/* 自动补充保证金 */}
                  <div className="rounded-lg border border-brown-100 bg-brown-50/40 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-brown-900 text-sm font-bold flex items-center gap-2">
                          <span className="w-4 h-4 flex items-center justify-center text-gold-600">
                            <i className="ri-flashlight-line text-sm"></i>
                          </span>
                          自动补充保证金
                        </h4>
                        <p className="text-brown-400 text-xs mt-1 leading-relaxed">
                          开启后，该用户保证金率跌到 30% 时，系统自动从其账户可用余额划钱补足保证金至 70%，避免被强平；余额不足则有多少补多少。
                        </p>
                      </div>
                      <button
                        onClick={handleToggleAutoMargin}
                        disabled={savingAutoMargin}
                        className={`relative inline-flex items-center w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                          autoMarginEnabled ? 'bg-gold-500' : 'bg-brown-200'
                        }`}
                      >
                        <span
                          className={`inline-block w-5 h-5 rounded-full bg-white transition-transform ${
                            autoMarginEnabled ? 'translate-x-6' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* 持仓订单 */}
                  <div>
                    <h4 className="text-brown-900 text-sm font-bold mb-3 flex items-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center text-gold-600">
                        <i className="ri-lock-line text-sm"></i>
                      </span>
                      持仓订单 ({detailData.positions.length})
                    </h4>
                    {detailData.positions.length === 0 ? (
                      <div className="bg-brown-50 rounded-lg p-4 text-center">
                        <p className="text-brown-400 text-xs">暂无持仓订单</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-brown-100">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-brown-400 text-xs bg-brown-50">
                              <th className="px-3 py-2 font-medium">品种</th>
                              <th className="px-3 py-2 font-medium">方向</th>
                              <th className="px-3 py-2 font-medium">克重</th>
                              <th className="px-3 py-2 font-medium">开仓价</th>
                              <th className="px-3 py-2 font-medium">当前价</th>
                              <th className="px-3 py-2 font-medium">实时盈亏</th>
                              <th className="px-3 py-2 font-medium">保证金</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailData.positions.map((p) => (
                              <tr key={p.id} className="border-t border-brown-50">
                                <td className="px-3 py-2.5 text-brown-900 font-medium">{p.name}</td>
                                <td className="px-3 py-2.5">
                                  <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${p.direction === 'IN' ? 'bg-red-50 text-red-500' : 'bg-emerald-50 text-emerald-600'}`}>
                                    {p.direction === 'IN' ? '买入' : '卖出'}
                                  </span>
                                </td>
                                <td className="px-3 py-2.5 text-brown-700">{p.weight}g</td>
                                <td className="px-3 py-2.5 text-brown-700">{p.open_price.toFixed(2)}</td>
                                <td className="px-3 py-2.5 text-brown-700">{p.current_price.toFixed(2)}</td>
                                <td className={`px-3 py-2.5 font-medium ${p.live_profit >= 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                                  {p.live_profit >= 0 ? '+' : ''}{p.live_profit.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                                </td>
                                <td className="px-3 py-2.5 text-brown-700">
                                  ¥{p.original_margin.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* 实物资产 */}
                  {detailData.physical_assets.length > 0 && (
                    <div>
                      <h4 className="text-brown-900 text-sm font-bold mb-3 flex items-center gap-2">
                        <span className="w-4 h-4 flex items-center justify-center text-gold-600">
                          <i className="ri-vip-diamond-line text-sm"></i>
                        </span>
                        实物资产 ({detailData.physical_assets.length})
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {detailData.physical_assets.map((a) => (
                          <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg border border-brown-100 bg-brown-50/30">
                            <div className="w-10 h-10 rounded-lg bg-gold-100 flex items-center justify-center flex-shrink-0">
                              <span className="w-5 h-5 flex items-center justify-center text-gold-700">
                                <i className="ri-vip-diamond-fill text-base"></i>
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-brown-900 text-sm font-medium">{a.product}</p>
                              <p className="text-brown-400 text-xs mt-0.5">{a.weight}g · {a.status}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-brown-400 text-[10px]">{a.location || '金库'}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
      {/* 删除用户确认弹窗 */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (!deleting) {
                setDeleteTarget(null);
                setDeleteConfirmText('');
              }
            }}
          ></div>
          <div className="relative bg-white rounded-xl w-full max-w-md overflow-hidden animate-fade-up">
            <div className="px-5 py-4 border-b border-brown-100">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-red-50 text-red-500">
                  <i className="ri-delete-bin-line text-lg"></i>
                </span>
                <div>
                  <h3 className="text-brown-900 text-base font-bold">删除用户</h3>
                  <p className="text-brown-400 text-xs mt-0.5">此操作不可撤销</p>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-4">
              <div className="rounded-lg bg-red-50 border border-red-100 p-3">
                <p className="text-red-600 text-xs leading-relaxed">
                  即将永久删除用户 <strong>{deleteTarget.name || '未填写姓名'}</strong>（{deleteTarget.phone || deleteTarget.email || '无联系方式'}）的所有数据，包括持仓订单、资产、入金出金记录等，且无法恢复。
                </p>
              </div>

              <div>
                <p className="text-brown-600 text-xs mb-1.5">
                  请输入 <span className="text-red-500 font-medium">删除</span> 以确认
                </p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="删除"
                  disabled={deleting}
                  className="w-full px-3 py-2 rounded-lg border border-brown-200 bg-white text-sm text-brown-800 outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-50"
                />
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => {
                    if (!deleting) {
                      setDeleteTarget(null);
                      setDeleteConfirmText('');
                    }
                  }}
                  disabled={deleting}
                  className="flex-1 inline-flex items-center justify-center px-4 py-2 rounded-md border border-brown-200 text-brown-600 text-sm cursor-pointer whitespace-nowrap hover:bg-brown-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  取消
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting || deleteConfirmText !== '删除'}
                  className="flex-1 inline-flex items-center justify-center px-4 py-2 rounded-md bg-red-500 text-white text-sm cursor-pointer whitespace-nowrap hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? '删除中...' : '确认删除'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* 身份证图片放大预览 */}
      {previewImage && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setPreviewImage(null)}></div>
          <div className="relative bg-white rounded-xl overflow-hidden max-w-2xl w-full max-h-[90vh] animate-fade-up">
            <div className="flex items-center justify-between px-4 py-3 border-b border-brown-100">
              <span className="text-brown-900 text-sm font-bold">身份证图片</span>
              <button
                onClick={() => setPreviewImage(null)}
                className="w-8 h-8 flex items-center justify-center text-brown-400 hover:text-brown-600 cursor-pointer rounded-full hover:bg-brown-50 transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <img src={previewImage} alt="身份证大图" className="w-full max-h-[80vh] object-contain bg-brown-50" />
          </div>
        </div>
      )}
    </div>
  );
}